import { decode } from "html-entities";
import { marked } from "marked";

const rawBlockPattern = /<(script|style|iframe|object|embed)\b[^>]*>[\s\S]*?<\/\1>/gi;

function escapeHtml(value = "") {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function isSafeUrl(href = "") {
  const trimmedHref = String(href).trim();
  if (!trimmedHref) return false;
  if (
    trimmedHref.startsWith("/") ||
    trimmedHref.startsWith("#") ||
    trimmedHref.startsWith("?")
  ) {
    return true;
  }

  try {
    const parsedUrl = new URL(trimmedHref);
    return ["http:", "https:", "mailto:", "tel:"].includes(parsedUrl.protocol);
  } catch {
    return false;
  }
}

const NEW_TAB_LINK_ATTRS = 'target="_blank" rel="noopener noreferrer"';

// Raw HTML in authored text keeps bare inline formatting and plain images;
// anything else (handlers, unknown tags, unquoted attributes) is dropped.
const INLINE_TAGS = new Set(["u", "b", "i", "em", "strong", "s", "sup", "sub", "br"]);
const IMAGE_ATTRIBUTES = new Set(["src", "alt", "title", "width", "height"]);
const QUOTED_ATTRIBUTE = /([a-zA-Z]+)\s*=\s*"([^"]*)"/g;
const DATA_IMAGE = /^data:image\/(png|jpe?g|gif|webp);base64,[A-Za-z0-9+/=\s]+$/i;

function isSafeImageSrc(src = "") {
  return DATA_IMAGE.test(String(src).trim()) || isSafeUrl(src);
}

function renderImage({ src, alt = "", title, width, height }) {
  if (!isSafeImageSrc(src)) return escapeHtml(alt);
  const size = [["width", width], ["height", height]]
    .filter(([, value]) => /^\d{1,4}$/.test(value || ""))
    .map(([name, value]) => ` ${name}="${value}"`).join("");
  const titleAttr = title ? ` title="${escapeHtml(title)}"` : "";
  return `<img src="${escapeHtml(src)}" alt="${escapeHtml(alt)}"${titleAttr}${size}>`;
}

function renderRawHtml(raw) {
  const text = String(raw).trim();
  const bare = text.match(/^<(\/?)([a-z]+)\s*\/?>$/i);
  if (bare) return INLINE_TAGS.has(bare[2].toLowerCase()) ? `<${bare[1]}${bare[2].toLowerCase()}>` : "";
  const image = text.match(/^<img\b([^<>]*?)\/?>$/i);
  if (!image || image[1].replace(QUOTED_ATTRIBUTE, "").trim()) return "";
  // Values are read as the browser would (`&amp;` in a signed URL is `&`),
  // then validated and escaped once on the way out.
  const attributes = Object.fromEntries([...image[1].matchAll(QUOTED_ATTRIBUTE)]
    .map(([, name, value]) => [name.toLowerCase(), decode(value, { scope: "attribute" })]));
  if (Object.keys(attributes).some(name => !IMAGE_ATTRIBUTES.has(name))) return "";
  return renderImage(attributes);
}

function createRenderer({ openLinksInNewTab, linkifyCodeUrls }) {
  const opensInNewTab = href => !href.startsWith("#") && (
    typeof openLinksInNewTab === "function" ? openLinksInNewTab(href) : openLinksInNewTab
  );
  const renderer = new marked.Renderer();
  renderer.html = token => renderRawHtml(token.text);
  renderer.link = function link(token) {
    const text = this.parser.parseInline(token.tokens);
    if (!isSafeUrl(token.href)) return text;
    const title = token.title ? ` title="${escapeHtml(token.title)}"` : "";
    const extra = opensInNewTab(token.href) ? ` ${NEW_TAB_LINK_ATTRS}` : "";
    return `<a${extra} href="${escapeHtml(token.href)}"${title}>${text}</a>`;
  };
  renderer.image = token => renderImage({ src: token.href, alt: token.text || "", title: token.title });
  if (linkifyCodeUrls) {
    const defaultCodespan = renderer.codespan.bind(renderer);
    renderer.codespan = token => {
      const href = String(token.text || "").trim();
      let safe = false;
      try {
        const parsed = new URL(href, "https://salesably.internal");
        safe = /^https?:\/\//i.test(href) || (/^\/(?!\/)/.test(href) && parsed.origin === "https://salesably.internal");
      } catch { /* A code span need not be a URL. */ }
      if (!safe || !isSafeUrl(href)) return defaultCodespan(token);
      return `<a${opensInNewTab(href) ? ` ${NEW_TAB_LINK_ATTRS}` : ""} href="${escapeHtml(href)}">Open link</a>`;
    };
  }
  return renderer;
}

function stripRawBlocks(content) {
  let strippedContent = content;
  let previousContent;

  do {
    previousContent = strippedContent;
    strippedContent = strippedContent.replace(rawBlockPattern, "");
  } while (strippedContent !== previousContent);

  return strippedContent;
}

// Markdown → sanitized HTML for dangerouslySetInnerHTML. This must run in both
// server and client components; keep it free of jsdom-backed sanitizers so
// server-rendered routes do not load browser emulation packages in production.
// `openLinksInNewTab` accepts a boolean or a predicate for selected destinations.
// `breaks` turns a single newline into a line break, for plain text authored
// without blank lines between paragraphs (scenario scripts, openings).
export function renderSanitizedMarkdown(content = "", { breaks = false, openLinksInNewTab = false, linkifyCodeUrls = false } = {}) {
  const renderer = createRenderer({ openLinksInNewTab, linkifyCodeUrls });
  return marked(stripRawBlocks(String(content)), { renderer, breaks });
}
